import {CurrencyRuleConfigType, FXOrderFormType} from "../../../types/fx.type";
import {UserBankAccountType} from "../../../types/user.type";

// Only outstanding balances in these source currencies are subject to the
// conversion threshold restriction.
const RESTRICTED_SOURCE_CURRENCIES = ["IDR", "THB"];

// The restriction only applies when converting the above balances to MYR.
const RESTRICTED_TARGET_CURRENCY = "MYR";

/**
 * Determines whether the current conversion is a potential speculative order.
 *
 * The restriction only applies when converting an IDR / THB balance to MYR
 * (IDR/THB -> MYR). The source must be IDR or THB and the target must be MYR.
 * The reverse direction (MYR -> IDR/THB) is not restricted.
 *
 * The threshold is hit only when BOTH conditions in the matching rule are true:
 *  a) The MYR (target) amount is greater than `to_threshold` (e.g. 5,000).
 *  b) The IDR/THB (source) amount is >= `balance_percentage` (e.g. 50%) of the
 *     outstanding balance of that IDR/THB currency.
 *
 * Rules, thresholds and percentages are fully driven by the backend response
 * (`currencyRulesConfig`); nothing is hard coded here.
 *
 * @param fxOrderForm - The current FX order form.
 * @param accounts - The user's bank accounts.
 * @param rules - The backend-provided currency rule configs.
 * @returns {boolean} - True when the conversion should be blocked, otherwise false.
 */
export const isConversionThresholdExceeded = (
    fxOrderForm: FXOrderFormType,
    accounts: UserBankAccountType[],
    rules: CurrencyRuleConfigType[]
): boolean => {
    if (!rules || rules.length === 0) return false;

    const sourceCurrency = fxOrderForm.source.currency;
    const targetCurrency = fxOrderForm.target.currency;

    if (!sourceCurrency || !targetCurrency) return false;

    // Only IDR/THB -> MYR is restricted. The source must be IDR/THB and the
    // target must be MYR; the reverse direction is not restricted.
    if (!RESTRICTED_SOURCE_CURRENCIES.includes(sourceCurrency)) return false;
    if (targetCurrency !== RESTRICTED_TARGET_CURRENCY) return false;
    const restrictedCurrency = sourceCurrency;

    // Find a matching backend rule for this currency pair.
    const rule = rules.find(
        (v) =>
            v.enabled &&
            v.from_currency === sourceCurrency &&
            v.to_currency === targetCurrency
    );
    if (!rule) return false;

    // Amount on the MYR (target) side and the IDR/THB (source) side.
    const myrAmount = fxOrderForm.target.amount.value;
    const restrictedAmount = fxOrderForm.source.amount.value;

    // Total outstanding balance of the IDR/THB currency.
    const outstandingBalance = accounts
        .filter((v) => v.currency === restrictedCurrency)
        .reduce((sum, v) => sum + v.balance, 0);

    if (outstandingBalance <= 0) return false;

    // The MYR amount is computed as source * rate, so it carries floating-point
    // noise (e.g. 5000.0000001). Round to MYR currency precision so a displayed
    // 5,000.00 is treated as exactly 5,000 and allowed to proceed.
    const roundedMyrAmount = Number(myrAmount.toFixed(2));

    const exceedsTargetThreshold = roundedMyrAmount > rule.to_threshold;
    const balanceThreshold = outstandingBalance * rule.balance_percentage;
    const balanceTolerance = Number.EPSILON * Math.max(
        Math.abs(restrictedAmount),
        Math.abs(balanceThreshold)
    ) * 4;
    const exceedsBalancePercentage =
        restrictedAmount >= balanceThreshold ||
        balanceThreshold - restrictedAmount <= balanceTolerance;

    return exceedsTargetThreshold && exceedsBalancePercentage;
};
